import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, DollarSign, ArrowRightLeft, Clock, User, ChevronDown } from 'lucide-react';
import { productHistoryAPI } from '../../services/garageApi';

const actionConfig = {
  created: { icon: Plus, color: 'bg-green-100 text-green-600', label: 'Created' },
  edited: { icon: Edit, color: 'bg-blue-100 text-blue-600', label: 'Edited' },
  price_updated: { icon: DollarSign, color: 'bg-yellow-100 text-yellow-600', label: 'Price Updated' },
  stock_moved: { icon: ArrowRightLeft, color: 'bg-purple-100 text-purple-600', label: 'Stock Moved' },
};

const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatFieldName = (field) => {
  return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const HistoryEntry = ({ entry }) => {
  const config = actionConfig[entry.action] || actionConfig.edited;
  const Icon = config.icon;

  const renderDescription = () => {
    if (entry.action === 'created') {
      return <span>Product was created</span>;
    }
    if (entry.action === 'stock_moved' && entry.metadata) {
      const m = entry.metadata;
      return (
        <span>
          Moved <strong>{m.quantity}</strong> units from {m.from_location}/{m.from_zone} to {m.to_location}/{m.to_zone}
        </span>
      );
    }
    if (entry.field_changed) {
      return (
        <span>
          <strong>{formatFieldName(entry.field_changed)}</strong> changed from{' '}
          <span className="line-through text-gray-400">{entry.old_value || 'empty'}</span> to{' '}
          <strong>{entry.new_value}</strong>
        </span>
      );
    }
    return <span>{config.label}</span>;
  };

  return (
    <div className="flex gap-3 pb-4 last:pb-0">
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="w-px flex-1 bg-gray-200 mt-1" />
      </div>
      <div className="flex-1 pb-4">
        <div className="flex items-start justify-between">
          <div className="text-sm text-gray-800">{renderDescription()}</div>
          <span className="text-xs text-gray-400 flex-shrink-0 ml-2 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTimeAgo(entry.created_at)}
          </span>
        </div>
        {entry.reason && (
          <p className="text-xs text-gray-500 mt-1 italic">Reason: {entry.reason}</p>
        )}
        {entry.user && (
          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
            <User className="w-3 h-3" /> {entry.user.name}
          </p>
        )}
      </div>
    </div>
  );
};

const ProductHistoryTimeline = ({ productType, productId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchHistory = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const result = await productHistoryAPI.getByProduct(productType, productId, {
        page: pageNum,
        per_page: 15,
      });

      const items = Array.isArray(result.data) ? result.data : (result.data?.data || []);
      if (append) {
        setHistory((prev) => [...prev, ...items]);
      } else {
        setHistory(items);
      }
      setHasMore(result.pagination?.current_page < result.pagination?.last_page);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [productType, productId]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchHistory(nextPage, true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No history yet</p>
        <p className="text-sm mt-1">Changes to this product will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {history.map((entry, i) => (
        <HistoryEntry key={entry.id || i} entry={entry} />
      ))}
      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1"
        >
          <ChevronDown className="w-4 h-4" />
          {loadingMore ? 'Loading...' : 'Load More'}
        </button>
      )}
    </div>
  );
};

export default ProductHistoryTimeline;
